// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewGroupSection.test.tsx
//
// One group of the review's open findings (warhol spec dave.savostyanov/0090):
// its letter, name and count, how many must be fixed, the points on what is
// wrong in it -- read with the group closed -- and, open, its findings most
// serious first.
import { CaretDown } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { type PinGroup, severityRank } from "./pinGroups";

/** The group's name as she reads it: its block, and where it sits when two groups share the block. */
export function usePinGroupName() {
  const { t } = useTranslation("ui");
  return (group: PinGroup) => {
    const name = t(`askV2.review.pinGroups.block.${group.block}`);
    if (!group.place) return name;
    return t("askV2.review.pinGroups.placeName", {
      name,
      row: t(`askV2.review.pinGroups.row.${group.place.row}`),
      col: t(`askV2.review.pinGroups.col.${group.place.col}`),
    });
  };
}

export function AskV2ReviewGroupSection({
  group,
  mark,
  open,
  onToggle,
  children,
}: {
  group: PinGroup;
  /** The group's letter, the same as on its pin. */
  mark: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation("ui");
  const nameOf = usePinGroupName();
  const urgent = group.members.filter((m) => severityRank(m) === 2).length;
  const listId = `ask-v2-pin-group-list-${group.key}`;
  return (
    <li className="mb-2" data-testid={`ask-v2-pin-group-${group.key}`}>
      <button
        type="button"
        className="w-full rounded-lg border border-puntt-neutral-gray-5 bg-base-white p-3 text-left hover:bg-puntt-neutral-gray-2"
        data-testid={`ask-v2-pin-group-toggle-${group.key}`}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        onClick={onToggle}
      >
        <span className="flex items-center gap-2">
          <span
            className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold ${
              group.urgent
                ? "bg-puntt-accent-9 text-base-white"
                : "border border-puntt-accent-7 bg-puntt-accent-4 text-puntt-accent-11"
            }`}
            data-testid={`ask-v2-pin-group-number-${group.key}`}
            data-urgent={group.urgent ? "true" : undefined}
          >
            {mark}
          </span>
          <Text
            as="span"
            size="2"
            weight="medium"
            className="min-w-0 truncate text-puntt-neutral-gray-12"
          >
            {nameOf(group)}
          </Text>
          <span
            className="ml-auto shrink-0 text-[12px] tabular-nums text-puntt-neutral-gray-10"
            data-testid={`ask-v2-pin-group-count-${group.key}`}
          >
            {group.members.length}
          </span>
          <CaretDown
            size={14}
            aria-hidden="true"
            className={`shrink-0 text-puntt-neutral-gray-11 transition-transform ${open ? "" : "-rotate-90"}`}
          />
        </span>
        {/* why on every group: warhol's line says what is wrong, not how much of it must be fixed. */}
        <Text
          as="p"
          size="1"
          className="mt-1 text-puntt-neutral-gray-10"
          data-testid={`ask-v2-pin-group-counted-${group.key}`}
        >
          {t("askV2.review.pinGroups.counted", {
            urgent,
            other: group.members.length - urgent,
          })}
        </Text>
        {group.points ? (
          // why spans and not <ul>: the points sit inside the toggle <button>, which may hold only phrasing content.
          <span
            className="mt-1.5 block space-y-0.5"
            data-testid={`ask-v2-pin-group-summary-${group.key}`}
          >
            {group.points.map((point, index) => (
              <Text
                key={`${index}-${point}`}
                as="span"
                size="2"
                className="flex gap-1.5 text-puntt-neutral-gray-11"
                data-testid={`ask-v2-pin-group-point-${group.key}-${index}`}
              >
                <span aria-hidden="true">•</span>
                <span className="min-w-0">{point}</span>
              </Text>
            ))}
          </span>
        ) : null}
      </button>
      {open ? (
        <ul
          id={listId}
          className="ml-4 mt-2"
          data-testid={`ask-v2-pin-group-members-${group.key}`}
        >
          {children}
        </ul>
      ) : null}
    </li>
  );
}
